import React, { FunctionComponent } from 'react';
import get from 'lodash/get';
import { Controller, useFormContext, UseFormReturn } from 'react-hook-form';
import { ControllerRenderProps, UseControllerProps } from 'react-hook-form/dist/types/controller';

import { FormGroup, FormGroupEssentialProps } from '../../../components';

type BasicProps<DefaultValue = unknown> = FormGroupEssentialProps & {
  name: string;
  rules?: UseControllerProps['rules'];
  defaultValue?: DefaultValue;
};

export type ConnectedComponentProps<ComponentProps extends BasicProps> = {
  controllerField?: ControllerRenderProps;
  componentProps: ComponentProps;
};

export type ConnectHelperProps<ComponentProps extends BasicProps, DefaultValue = unknown> = BasicProps<DefaultValue> & {
  RenderComponent: FunctionComponent<ConnectedComponentProps<ComponentProps>>;
};

export function ConnectHelper<ComponentProps extends BasicProps, DefaultValue = unknown>({
  name,
  label,
  helper,
  isLoading,
  inline,
  extendedClassNames,
  valid,
  rules,
  RenderComponent,
  defaultValue,
  ...props
}: ConnectHelperProps<ComponentProps, DefaultValue>) {
  const context = useFormContext() as UseFormReturn | null;

  const errors = get(context?.formState.errors, name, null);

  return (
    <FormGroup
      valid={errors === undefined ? undefined : false}
      validationMessage={errors?.message}
      extendedClassNames={extendedClassNames}
      label={label}
      helper={helper}
      isLoading={isLoading}
      inline={inline}
    >
      {context ? (
        <Controller
          control={context.control}
          defaultValue={defaultValue}
          name={name}
          rules={rules}
          render={({ field }) => (
            <RenderComponent
              controllerField={field}
              componentProps={{ valid: errors ? false : valid, ...props } as ComponentProps}
            />
          )}
        />
      ) : (
        <RenderComponent componentProps={{ valid: errors ? false : valid, ...props } as ComponentProps} />
      )}
    </FormGroup>
  );
}
